import { Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { getMergeOptions } from './course-utils';
import type { CourseMapRoot, CourseNodeBase, NodeEntity } from './coursemap.types';

interface MergePopoverProps {
	node: NodeEntity;
	screenX: number;
	screenY: number;
	isAddingCourse: boolean;
	updateTreeData: (obj: CourseMapRoot | CourseNodeBase, label?: string) => void;
	onClose: () => void;
}

const POPOVER_WIDTH = 280;

const rootNode = (n: NodeEntity) => {
	const ancestors = n.ancestors();
	return ancestors[ancestors.length - 1];
};

/** Nodes elsewhere in the tree that list `hub` as a supplemental parent (drawn as links into this course). */
function incomingMergeNodes(hub: NodeEntity): NodeEntity[] {
	const tracker = hub.data.tracker;
	if (!tracker) return [];
	const root = rootNode(hub);
	return root
		.descendants()
		.filter(
			(d) =>
				d !== root &&
				d.data.tracker !== tracker &&
				(d.data.suppParent ?? []).some((p) => p.tracker === tracker)
		)
		.sort((a, b) => a.data.name.localeCompare(b.data.name));
}

const MergePopover = ({
	node,
	screenX,
	screenY,
	isAddingCourse,
	updateTreeData,
	onClose,
}: MergePopoverProps) => {
	const popoverRef = useRef<HTMLDivElement>(null);

	// Close on outside pointer-down
	useEffect(() => {
		const handlePointerDown = (e: PointerEvent) => {
			if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
				onClose();
			}
		};
		document.addEventListener('pointerdown', handlePointerDown);
		return () => document.removeEventListener('pointerdown', handlePointerDown);
	}, [onClose]);

	// Close on Escape
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') onClose();
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [onClose]);

	// Hub = the node whose merge button opened the popover (matches header subtitle).
	const hub = node;
	const courseOptions = getMergeOptions(hub);
	const outgoingSuppParents = hub.data.suppParent ?? [];
	const incomingNodes = useMemo(() => incomingMergeNodes(hub), [hub]);

	const chooseCourse = useCallback(
		(target: NodeEntity) => {
			const { id, name, tracker } = target.data;
			if (hub.data.suppParent === undefined) hub.data.suppParent = [];
			const ref: CourseNodeBase = { id, name, tracker, children: [] };
			hub.data.suppParent.push(ref);
			const root = rootNode(target);
			updateTreeData({ ...root.data }, `Merged '${hub.data.name}' → '${name}'`);
			onClose();
		},
		[hub, updateTreeData, onClose]
	);

	const removeOutgoingParent = useCallback(
		(parent: { tracker: string }) => {
			hub.data.suppParent = (hub.data.suppParent ?? []).filter((p) => p.tracker !== parent.tracker);
			if (hub.data.suppParent.length === 0) delete hub.data.suppParent;
			const root = rootNode(hub);
			updateTreeData({ ...root.data }, `Removed merge link on '${hub.data.name}'`);
		},
		[hub, updateTreeData]
	);

	const removeIncomingLink = useCallback(
		(child: NodeEntity) => {
			const tracker = hub.data.tracker;
			const next = (child.data.suppParent ?? []).filter((p) => p.tracker !== tracker);
			if (next.length === 0) delete child.data.suppParent;
			else child.data.suppParent = next;
			const root = rootNode(hub);
			updateTreeData(
				{ ...root.data },
				`Removed merge link from '${child.data.name}' to '${hub.data.name}'`
			);
		},
		[hub, updateTreeData]
	);

	// ── Clamp to viewport ───────────────────────────────────────────────────────
	const vpWidth = window.innerWidth;
	const vpHeight = window.innerHeight;
	// Prefer left of the merge button; fall back to right if off-screen
	const leftOfButton = screenX - POPOVER_WIDTH - 8;
	const left =
		leftOfButton >= 8 ? leftOfButton : Math.min(screenX + 8, vpWidth - POPOVER_WIDTH - 8);
	const top = Math.min(Math.max(screenY, 8), vpHeight - 320);

	const showAvailable = isAddingCourse && courseOptions.length > 0;
	const hasOutgoing = outgoingSuppParents.length > 0;
	const hasIncoming = incomingNodes.length > 0;
	const hasExisting = hasOutgoing || hasIncoming;

	return (
		<div
			ref={popoverRef}
			style={{
				position: 'fixed',
				left,
				top,
				width: POPOVER_WIDTH,
				zIndex: 1000,
			}}
			className="flex flex-col overflow-hidden rounded-xl border border-border bg-(--panel-color) shadow-2xl"
		>
			{/* ── Header ───────────────────────────────────────────────────── */}
			<div className="flex items-center justify-between border-b px-3 py-2.5">
				<div className="flex flex-col gap-0.5">
					<span className="text-sm font-semibold leading-none">Merge Links</span>
					{node.data.name && (
						<span className="text-xs text-muted-foreground leading-none mt-0.5 truncate max-w-[180px]">
							{node.data.name}
						</span>
					)}
				</div>
				<button
					type="button"
					onClick={onClose}
					className="rounded-md p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
					aria-label="Close"
				>
					<X size={15} />
				</button>
			</div>

			<div className="flex-1 overflow-y-auto" style={{ maxHeight: 320 }}>
				{/* ── Available merge targets ───────────────────────────────── */}
				{isAddingCourse && (
					<div>
						<p className="px-3 pt-2.5 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
							Available
						</p>
						{showAvailable ? (
							courseOptions.map((opt) => (
								<button
									type="button"
									key={`${opt.data.tracker}-available`}
									onClick={() => chooseCourse(opt)}
									className="flex w-full items-center px-3 py-2.5 text-left hover:bg-accent transition-colors"
								>
									<span className="text-sm font-medium">{opt.data.name}</span>
								</button>
							))
						) : (
							<p className="px-3 py-2 text-sm text-muted-foreground">No available merges</p>
						)}
					</div>
				)}

				{/* Divider between sections when both show */}
				{isAddingCourse && <div className="border-t mx-3 my-1" />}

				{/* ── Existing merge links ──────────────────────────────────── */}
				<div>
					<p className="px-3 pt-2.5 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
						Existing
					</p>
					{hasExisting ? (
						<>
							{hasOutgoing && (
								<div className="pb-1">
									<p className="px-3 pb-1 text-[11px] font-medium text-muted-foreground">
										Additional parents
									</p>
									{outgoingSuppParents.map((parent) => (
										<div
											key={`out-${parent.tracker}`}
											className="flex items-center gap-2 px-3 py-2 group"
										>
											<span className="flex-1 text-sm font-medium truncate">{parent.name}</span>
											<button
												type="button"
												onClick={() => removeOutgoingParent(parent)}
												className="shrink-0 rounded-md p-1 text-muted-foreground opacity-0 group-hover:opacity-100 hover:bg-destructive/10 hover:text-destructive transition-all"
												aria-label={`Remove merge link to ${parent.name}`}
											>
												<Trash2 size={13} />
											</button>
										</div>
									))}
								</div>
							)}
							{hasOutgoing && hasIncoming && <div className="border-t mx-3 my-1" />}
							{hasIncoming && (
								<div className="pb-1">
									<p className="px-3 pb-1 text-[11px] font-medium text-muted-foreground">
										Linked from
									</p>
									{incomingNodes.map((child) => (
										<div
											key={`in-${child.data.tracker}`}
											className="flex items-center gap-2 px-3 py-2 group"
										>
											<span className="flex-1 text-sm font-medium truncate">{child.data.name}</span>
											<button
												type="button"
												onClick={() => removeIncomingLink(child)}
												className="shrink-0 rounded-md p-1 text-muted-foreground opacity-0 group-hover:opacity-100 hover:bg-destructive/10 hover:text-destructive transition-all"
												aria-label={`Remove merge link from ${child.data.name}`}
											>
												<Trash2 size={13} />
											</button>
										</div>
									))}
								</div>
							)}
						</>
					) : (
						<p className="px-3 py-2 pb-3 text-sm text-muted-foreground">No existing merges</p>
					)}
				</div>
			</div>
		</div>
	);
};

export default MergePopover;
